import { useEffect, useMemo, useState } from 'react';
import { Form, Input, Modal, Switch, notification } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import APIClient from '../api/api';
import { FEATURE_FLAG_WILDCARD_ACCOUNT_KEY, FeatureFlag, UpdateFeatureFlagRequest } from '../api/models';
import { Button, Callout, StatusTag } from '../components/ui';
import { PageHead } from '../layout';
import {
  defaultForUnlistedAccounts,
  fallbackAfterRemoving,
  FeatureFlagGroup,
  groupFeatureFlags,
  isActionCapable,
  isSelfPaused,
  isWildcardRow,
  keyKindLabels,
  removeOverrideRow,
  upsertFlagRow,
} from './FeatureFlags.model';

interface PendingEnable {
  group: FeatureFlagGroup;
  /** Existing row being re-enabled, when there is one. */
  row?: FeatureFlag;
  scope: 'global' | 'account';
  accountKey: string;
}

interface PendingRemove {
  group: FeatureFlagGroup;
  row: FeatureFlag;
}

interface OverrideModalState {
  group: FeatureFlagGroup;
  existing?: FeatureFlag;
}

function flagLabel(group: FeatureFlagGroup, accountKey: string): string {
  const title = group.descriptor?.title ?? group.name;
  if (accountKey === '') return title;
  if (accountKey === FEATURE_FLAG_WILDCARD_ACCOUNT_KEY) return `${title} for all other accounts`;
  return `${title} for ${accountKey}`;
}

function formatUpdated(row: FeatureFlag): string {
  const when = dayjs(row.updatedAt).format('YYYY-MM-DD HH:mm');
  return row.updatedBy ? `${when} by ${row.updatedBy}` : when;
}

const FeatureFlags = () => {
  const [rows, setRows] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingEnable, setPendingEnable] = useState<PendingEnable | null>(null);
  const [enableReason, setEnableReason] = useState('');
  const [pendingRemove, setPendingRemove] = useState<PendingRemove | null>(null);
  const [overrideModal, setOverrideModal] = useState<OverrideModalState | null>(null);
  const [saving, setSaving] = useState(false);
  const [overrideForm] = Form.useForm();

  const groups = useMemo(() => groupFeatureFlags(rows), [rows]);

  useEffect(() => {
    loadFlags();
  }, []);

  async function loadFlags() {
    setLoading(true);
    try {
      setRows(await APIClient.listFeatureFlags());
    } catch (e) {
      notification.error({ message: 'Failed to load feature flags.' });
    } finally {
      setLoading(false);
    }
  }

  /** Returns null when the change was saved, otherwise the server's message. */
  async function applyChange(
    req: UpdateFeatureFlagRequest,
    successMessage: string,
  ): Promise<string | null> {
    setSaving(true);
    try {
      const row = await APIClient.updateFeatureFlag(req);
      setRows((prev) => upsertFlagRow(prev, row));
      notification.success({ message: successMessage });
      return null;
    } catch (e: any) {
      const errorMsg = e?.response?.data?.error || 'Failed to update feature flag.';
      notification.error({ message: errorMsg });
      return errorMsg;
    } finally {
      setSaving(false);
    }
  }

  function handleToggle(group: FeatureFlagGroup, row: FeatureFlag | undefined, scope: 'global' | 'account', accountKey: string, next: boolean) {
    if (next) {
      setEnableReason('');
      setPendingEnable({ group, row, scope, accountKey });
      return;
    }
    applyChange(
      { name: group.name, scope, accountKey, enabled: false },
      `${flagLabel(group, accountKey)} disabled.`,
    );
  }

  async function handleConfirmEnable() {
    if (!pendingEnable) return;
    const { group, scope, accountKey } = pendingEnable;
    const error = await applyChange(
      { name: group.name, scope, accountKey, enabled: true, reason: enableReason.trim() },
      `${flagLabel(group, accountKey)} enabled.`,
    );
    if (!error) setPendingEnable(null);
  }

  async function handleConfirmRemove() {
    if (!pendingRemove) return;
    const { group, row } = pendingRemove;
    setSaving(true);
    try {
      await APIClient.deleteFeatureFlagOverride({ name: group.name, accountKey: row.accountKey });
      setRows((prev) => removeOverrideRow(prev, group.name, row.accountKey));
      notification.success({
        message: isWildcardRow(row)
          ? 'Default for all other accounts removed.'
          : `Override for ${row.accountKey} removed.`,
      });
      setPendingRemove(null);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setRows((prev) => removeOverrideRow(prev, group.name, row.accountKey));
        notification.info({ message: 'Override was already removed.' });
        setPendingRemove(null);
      } else {
        const errorMsg = e?.response?.data?.error || 'Failed to remove override.';
        notification.error({ message: errorMsg });
      }
    } finally {
      setSaving(false);
    }
  }

  function openOverrideModal(group: FeatureFlagGroup, existing?: FeatureFlag) {
    setOverrideModal({ group, existing });
    overrideForm.setFieldsValue({
      accountKey: existing?.accountKey ?? '',
      enabled: existing?.enabled ?? false,
      reason: existing?.reason ?? '',
    });
  }

  async function handleSaveOverride() {
    if (!overrideModal) return;
    let values;
    try {
      values = await overrideForm.validateFields();
    } catch {
      return;
    }
    const accountKey: string = values.accountKey.trim();
    const error = await applyChange(
      {
        name: overrideModal.group.name,
        scope: 'account',
        accountKey,
        enabled: !!values.enabled,
        reason: (values.reason ?? '').trim(),
      },
      `${flagLabel(overrideModal.group, accountKey)} saved.`,
    );
    if (error) {
      // The server is the authority on which keys exist and which kind this
      // flag takes, so its rejection is shown against the field it is about.
      overrideForm.setFields([{ name: 'accountKey', errors: [error] }]);
      return;
    }
    setOverrideModal(null);
    overrideForm.resetFields();
  }

  function renderRowMeta(row: FeatureFlag) {
    return (
      <div className="text-[11px] text-text-3">
        {isSelfPaused(row) && (
          <StatusTag tone="danger">self-paused</StatusTag>
        )}
        {!row.enabled && row.reason && (
          <span className="ml-1.5">paused: {row.reason}</span>
        )}
        <span className="ml-1.5">updated {formatUpdated(row)}</span>
      </div>
    );
  }

  function renderUnlistedDefault(group: FeatureFlagGroup) {
    const fallback = defaultForUnlistedAccounts(group);
    const title = group.descriptor?.title ?? group.name;
    return (
      <div className="px-4 py-2.5 border-b border-border border-l-2 border-l-accent bg-bg-elevated">
        <div className="flex items-center gap-3">
          <Switch
            checked={fallback.enabled}
            onChange={(next) =>
              handleToggle(group, group.wildcard, 'account', FEATURE_FLAG_WILDCARD_ACCOUNT_KEY, next)
            }
            size="small"
            aria-label={`${title} default for all other accounts`}
          />
          <span className="text-[12.5px] font-medium text-text">All other accounts</span>
          {fallback.source === 'wildcard' ? (
            <>
              <StatusTag tone="accent" dot={false}>default row</StatusTag>
              {renderRowMeta(group.wildcard!)}
              <Button
                size="sm"
                variant="ghost"
                iconLeft={<DeleteOutlined />}
                className="ml-auto"
                onClick={() => setPendingRemove({ group, row: group.wildcard! })}
              >
                Remove
              </Button>
            </>
          ) : (
            <StatusTag tone="neutral" dot={false}>inherits global</StatusTag>
          )}
        </div>
        <div className="text-[11.5px] text-text-3 mt-1">
          {fallback.source === 'wildcard'
            ? 'Accounts without an override of their own take this row instead of the global row, so an account onboarded later stays at this setting until it is given an override.'
            : 'No default row — accounts without an override of their own follow the global row.'}
        </div>
      </div>
    );
  }

  function renderGroup(group: FeatureFlagGroup) {
    const actionCapable = isActionCapable(group.name);
    return (
      <div key={group.name} className="border border-border rounded-md bg-panel">
        <div className="px-4 pt-3 pb-2 border-b border-border">
          <div className="flex items-center gap-2.5">
            <span className="text-[14px] font-semibold text-text">
              {group.descriptor?.title ?? group.name}
            </span>
            <span className="text-[11px] font-mono text-text-3">{group.name}</span>
            {actionCapable ? (
              <StatusTag tone="warn" dot={false}>action-capable</StatusTag>
            ) : (
              <StatusTag tone="neutral" dot={false}>shadow — log only</StatusTag>
            )}
          </div>
          {group.descriptor && (
            <div className="text-[12.5px] text-text-3 mt-0.5">
              {actionCapable ? 'Enables' : 'While on'}: {group.descriptor.description}
            </div>
          )}
        </div>

        <div className="px-4 py-2.5 flex items-center gap-3 border-b border-border">
          <Switch
            checked={group.global?.enabled ?? false}
            onChange={(next) => handleToggle(group, group.global, 'global', '', next)}
            size="small"
            aria-label={`${group.descriptor?.title ?? group.name} global`}
          />
          <span className="text-[12.5px] font-medium text-text">Global</span>
          {group.global ? (
            renderRowMeta(group.global)
          ) : (
            <span className="text-[11px] text-text-3">never set — off</span>
          )}
        </div>

        {renderUnlistedDefault(group)}

        <div className="px-4 py-2.5">
          <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-1.5">
            Account overrides
          </div>
          {group.overrides.length === 0 && (
            <div className="text-[12px] text-text-3 mb-1.5">
              {group.wildcard
                ? 'No account overrides. Every account takes the default above.'
                : 'No account overrides. All accounts follow the global row.'}
            </div>
          )}
          {group.overrides.map((row) => (
            <div key={row.accountKey} className="flex items-center gap-3 py-1.5">
              <Switch
                checked={row.enabled}
                onChange={(next) => handleToggle(group, row, 'account', row.accountKey, next)}
                size="small"
                aria-label={`${group.descriptor?.title ?? group.name} for ${row.accountKey}`}
              />
              <span className="text-[12.5px] font-mono text-text">{row.accountKey}</span>
              {renderRowMeta(row)}
              <Button
                size="sm"
                variant="ghost"
                iconLeft={<EditOutlined />}
                className="ml-auto"
                onClick={() => openOverrideModal(group, row)}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                iconLeft={<DeleteOutlined />}
                onClick={() => setPendingRemove({ group, row })}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<PlusOutlined />}
            onClick={() => openOverrideModal(group)}
          >
            Add account override
          </Button>
        </div>
      </div>
    );
  }

  const pendingSelfPaused = pendingEnable?.row ? isSelfPaused(pendingEnable.row) : false;
  const removeFallback = pendingRemove
    ? fallbackAfterRemoving(pendingRemove.group, pendingRemove.row)
    : null;
  const overrideKeyKind = overrideModal?.group.descriptor
    ? keyKindLabels[overrideModal.group.descriptor.keyKind]
    : null;
  const removeFallbackEnables =
    !!pendingRemove && !pendingRemove.row.enabled && !!removeFallback?.enabled;

  return (
    <>
      <PageHead
        title="Feature Flags"
        subtitle="Kill switches for the fast transfer auto-opt-in pipeline. A global row switches a subsystem everywhere; an account row overrides it for one registrar or seller account, and the all-other-accounts row covers every account without an override, including accounts added later. An action flag also needs its global row on, so switching global off stops every account. A shadow flag switched on means observe-only, and a global shadow row holds every account there whatever its own row says."
      />
      <div className="flex-1 min-h-0 overflow-auto bg-panel p-6">
        <div className="mb-4">
          <Callout tone="warn" title="Check alerts before re-enabling">
            Subsystems switch their own flag off when they detect an anomaly and record an
            alert explaining why. Re-enabling a flag without reading that alert defeats the
            interlock. <Link to="/alerts" className="underline">Open alerts</Link>
          </Callout>
        </div>
        {loading && rows.length === 0 && (
          <div className="text-center py-12 text-text-3 text-[12px]">Loading…</div>
        )}
        <div className="flex flex-col gap-4">
          {groups.map(renderGroup)}
        </div>
      </div>

      <Modal
        title={pendingEnable ? `Enable ${flagLabel(pendingEnable.group, pendingEnable.accountKey)}?` : 'Enable flag?'}
        open={!!pendingEnable}
        onCancel={() => setPendingEnable(null)}
        onOk={handleConfirmEnable}
        okText="Enable"
        confirmLoading={saving}
        width={480}
      >
        {pendingEnable && (
          <div className="flex flex-col gap-3">
            {pendingEnable.group.descriptor && (
              <div className="text-[12.5px] text-text-2">
                Enables: {pendingEnable.group.descriptor.description}
              </div>
            )}
            {pendingSelfPaused ? (
              <Callout tone="danger" title="This flag paused itself">
                {pendingEnable.row?.reason
                  ? `Recorded reason: ${pendingEnable.row.reason}`
                  : 'An anomaly was detected.'}{' '}
                Read the matching alert before re-enabling.{' '}
                <Link to="/alerts" className="underline">Open alerts</Link>
              </Callout>
            ) : (
              <Callout tone="warn">
                {pendingEnable.row?.reason
                  ? `This flag is off with a recorded reason: ${pendingEnable.row.reason}. `
                  : 'If this flag was switched off automatically, an alert explains why. '}
                Check <Link to="/alerts" className="underline">alerts</Link> before enabling.
              </Callout>
            )}
            {isActionCapable(pendingEnable.group.name) && (
              <div className="text-[12px] text-text-3">
                This flag is action-capable: once enabled, the subsystem acts on live
                registrar or marketplace state.
              </div>
            )}
            <Input
              placeholder="Reason (recorded in the audit log)"
              value={enableReason}
              onChange={(e) => setEnableReason(e.target.value)}
            />
          </div>
        )}
      </Modal>

      <Modal
        title={
          pendingRemove
            ? isWildcardRow(pendingRemove.row)
              ? 'Remove the default for all other accounts?'
              : `Remove override for ${pendingRemove.row.accountKey}?`
            : 'Remove override?'
        }
        open={!!pendingRemove}
        onCancel={() => setPendingRemove(null)}
        onOk={handleConfirmRemove}
        okText="Remove"
        okButtonProps={{ danger: true }}
        confirmLoading={saving}
        width={480}
      >
        {pendingRemove && (
          <div className="flex flex-col gap-3">
            <div className="text-[12.5px] text-text-2">
              {isWildcardRow(pendingRemove.row)
                ? 'Every account without an override of its own falls back to the global row, which is'
                : `${pendingRemove.row.accountKey} falls back to ${
                    removeFallback?.source === 'wildcard'
                      ? 'the all-other-accounts default, which is'
                      : 'the global row, which is'
                  }`}
              {' '}currently {removeFallback?.enabled ? 'enabled' : 'off'}.
            </div>
            {removeFallbackEnables && (
              <Callout
                tone="danger"
                title={
                  isWildcardRow(pendingRemove.row)
                    ? 'Removing this default enables the flag for every account without an override'
                    : 'Removing this override enables the flag'
                }
              >
                {`${flagLabel(pendingRemove.group, pendingRemove.row.accountKey)} is off only
                because of this row. `}
                {isSelfPaused(pendingRemove.row)
                  ? 'It was written by an auto-pause trigger — read the matching alert before removing it. '
                  : ''}
                <Link to="/alerts" className="underline">Open alerts</Link>
              </Callout>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={
          overrideModal
            ? overrideModal.existing
              ? `Edit override: ${flagLabel(overrideModal.group, overrideModal.existing.accountKey)}`
              : `Add account override: ${overrideModal.group.descriptor?.title ?? overrideModal.group.name}`
            : 'Account override'
        }
        open={!!overrideModal}
        onCancel={() => {
          setOverrideModal(null);
          overrideForm.resetFields();
        }}
        onOk={handleSaveOverride}
        okText="Save"
        confirmLoading={saving}
        width={480}
      >
        {overrideModal && (
          <Form form={overrideForm} layout="vertical">
            <p className="text-text-3 text-[12px] mb-4">
              An account override wins over the all-other-accounts default, for that account
              only. It does not outrank the global row: an action flag still needs global on,
              and a shadow flag stays observe-only while the global shadow row is on. Remove
              the override to hand the account back to the default.
            </p>
            <Form.Item
              label={overrideKeyKind ? overrideKeyKind.label : 'Account key'}
              name="accountKey"
              extra={overrideKeyKind?.help}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: `${overrideKeyKind ? overrideKeyKind.label : 'Account key'} required`,
                },
                {
                  validator: (_, value) =>
                    (value ?? '').trim() === FEATURE_FLAG_WILDCARD_ACCOUNT_KEY
                      ? Promise.reject(
                          new Error('Use the all-other-accounts default instead of the wildcard key'),
                        )
                      : Promise.resolve(),
                },
              ]}
            >
              <Input
                placeholder={overrideKeyKind ? overrideKeyKind.placeholder : 'account key'}
                disabled={!!overrideModal.existing}
              />
            </Form.Item>
            <Form.Item label="Enabled" name="enabled" valuePropName="checked">
              <Switch size="small" />
            </Form.Item>
            <Form.Item label="Reason (recorded in the audit log)" name="reason">
              <Input placeholder="why this account differs from the global row" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default FeatureFlags;
